import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ClipboardCheck,
  Scan,
  HeartPulse,
  Brain,
  Shield,
  Users,
  MessageCircle,
  Mic,
  ArrowRight,
  Clock,
  UserCheck,
  Target,
  CheckCircle,
} from 'lucide-react';
import { services } from '../data/content';
import BookingModal from '../components/common/BookingModal';

const iconMap = {
  ClipboardCheck: ClipboardCheck,
  Scan: Scan,
  HeartPulse: HeartPulse,
  Brain: Brain,
  Shield: Shield,
  Users: Users,
  MessageCircle: MessageCircle,
  Mic: Mic,
};

const accentColors = [
  { bg: 'bg-teal-50', icon: 'text-teal-600', border: 'border-teal-200' },
  { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200' },
  { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-200' },
  { bg: 'bg-purple-50', icon: 'text-purple-600', border: 'border-purple-200' },
  { bg: 'bg-rose-50', icon: 'text-rose-600', border: 'border-rose-200' },
  { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200' },
];

const ServicesPage = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [expandedService, setExpandedService] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Handle hash navigation
    if (location.hash) {
      const id = location.hash.slice(1);
      setExpandedService(id);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  const toggleService = (id) => {
    setExpandedService(expandedService === id ? null : id);
  };

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'var(--gradient-hero-subtle)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge badge-teal mb-4">Our Services</span>
            <h1 className="heading-hero mb-6">Comprehensive Dysphagia Care</h1>
            <p className="body-large">
              From initial evaluation to ongoing treatment, we offer a full range of 
              services to help you swallow safely and confidently.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto space-y-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || ClipboardCheck;
              const colors = accentColors[index % accentColors.length];
              const isExpanded = expandedService === service.id;

              return (
                <div
                  key={service.id}
                  id={service.id}
                  className={`bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
                    isExpanded ? colors.border : 'border-gray-100 hover:border-gray-200'
                  }`}
                >
                  {/* Service Header */}
                  <button
                    onClick={() => toggleService(service.id)}
                    className="w-full p-6 flex items-start gap-4 text-left"
                  >
                    <div className={`w-14 h-14 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                      <IconComponent size={28} className={colors.icon} />
                    </div>
                    <div className="flex-grow">
                      <h2 className="heading-3 mb-1">{service.title}</h2>
                      <p className="body-small text-gray-600">{service.shortDescription}</p>
                    </div>
                    <div className={`w-8 h-8 rounded-full border flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${
                      isExpanded ? 'rotate-90 bg-gray-900 border-gray-900' : 'border-gray-300'
                    }`}>
                      <ArrowRight size={16} className={isExpanded ? 'text-white' : 'text-gray-500'} />
                    </div>
                  </button>

                  {/* Expanded Content */}
                  <div className={`transition-all duration-300 ${
                    isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="px-6 pb-6">
                      <div className="border-t border-gray-100 pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                          {/* Who It's For */}
                          <div className="bg-gray-50 rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <UserCheck size={18} className="text-teal-600" />
                              <h4 className="font-semibold text-gray-900">Who It's For</h4>
                            </div>
                            <p className="body-small text-gray-600">{service.whoIsItFor}</p>
                          </div>

                          {/* What to Expect */}
                          <div className="bg-gray-50 rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <CheckCircle size={18} className="text-teal-600" />
                              <h4 className="font-semibold text-gray-900">What to Expect</h4>
                            </div>
                            <p className="body-small text-gray-600">{service.whatToExpect}</p>
                          </div>

                          {/* Expected Outcomes */}
                          <div className="bg-gray-50 rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <Target size={18} className="text-teal-600" />
                              <h4 className="font-semibold text-gray-900">Expected Outcomes</h4>
                            </div>
                            <p className="body-small text-gray-600">{service.outcomes}</p>
                          </div>
                        </div>

                        {/* Duration & CTA */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-100">
                          <div className="flex items-center gap-2 text-gray-500">
                            <Clock size={16} />
                            <span className="text-sm">Duration: {service.duration}</span>
                          </div>
                          <button
                            onClick={() => setIsBookingOpen(true)}
                            className="btn-primary"
                          >
                            Schedule Evaluation
                            <ArrowRight size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'var(--gradient-hero-warm)' }}>
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-1 mb-4">Not Sure Which Service You Need?</h2>
            <p className="body-large mb-8">
              Start with a comprehensive evaluation. We'll assess your swallowing 
              and recommend the best treatment approach for you.
            </p>
            <button
              onClick={() => setIsBookingOpen(true)}
              className="btn-primary"
            >
              Book Your Evaluation
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} sourcePage="services" />
    </>
  );
};

export default ServicesPage;
